import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const FRAMEWORKS = [
  {
    slug: "from-self-to-system",
    title: "From Self to System",
    subtitle: "Inner worlds & the systems we create",
    quote:
      "A framework exploring the relationship between inner worlds and the systems we create.",
    body:
      "From personal awareness to collective impact, this framework explores how our inner states, beliefs and ways of relating quietly shape the cultures, communities and systems around us, and how we build and design accordingly.",
    cta: "Step into the experience",
    href: "https://essence-to-impact.emergent.host",
    external: true,
  },
  {
    slug: "architecture-of-community",
    title: "The Architecture of Community",
    subtitle: "Belonging, by design",
    quote:
      "Exploring the visible and invisible layers that create belonging and form a community.",
    body:
      "This framework investigates what allows communities and ecosystems to thrive beyond shared interests or proximity. It explores both visible structures, participation, spaces, governance and roles, and invisible foundations such as trust, care, identity, accountability and culture.",
    cta: "Step into the workshop",
    href: "https://harmonia-workshop.preview.emergentagent.com",
    external: true,
  },
  {
    slug: "your-inner-temple",
    title: "Your Inner Temple",
    subtitle: "A reflective journey to the landscapes within",
    quote: "A reflective journey to the landscapes within. The inner gateways.",
    body:
      "A living framework designed as a journey inward, exploring themes such as belonging, self-love, purpose, forgiveness, service and community. Built around a series of gateways, it invites deeper inquiry into the experiences, patterns and questions shaping who we become.",
    cta: "Request to learn more",
    external: false,
  },
  {
    slug: "your-journey",
    title: "Your Journey",
    subtitle: "A 4-door chapter",
    quote:
      "Aligning the inner world with the outer, so it can ripple into the collective.",
    body:
      "A 4-door chapter supporting leaders, creatives and people with a drive for impact and creation to align their inner world with their outer world, so it can ripple out to the collective and the systems they are part of.",
    cta: "View the booklet",
    href: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/uj7hb431_Your%20journey%20Booklet-4.pdf",
    external: true,
  },
];

export const Frameworks = () => {
  return (
    <section
      id="frameworks"
      data-testid="frameworks-section"
      className="relative bg-forest text-bone py-28 md:py-44 overflow-hidden"
    >
      {/* very subtle texture / orbital lines */}
      <svg className="absolute top-12 right-12 w-80 opacity-15 pointer-events-none" viewBox="0 0 400 400" fill="none">
        <circle cx="200" cy="200" r="180" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="2 6" />
        <circle cx="200" cy="200" r="120" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="1 3" />
        <circle cx="200" cy="200" r="60" stroke="#F4EFE6" strokeWidth="0.5" />
        <circle cx="380" cy="200" r="2" fill="#8C6A48" />
      </svg>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.12)}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-20"
        >
          <div>
            <motion.p variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-clay mb-4">
              ✦ &nbsp; 03 , Frameworks
            </motion.p>
            <motion.h2
              variants={fadeUp}
              data-testid="frameworks-title"
              className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight"
            >
              The lenses
              <br />
              <span className="italic text-bronze">I work through.</span>
            </motion.h2>
          </div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-bone/70 text-base md:text-lg font-light leading-relaxed"
          >
            These frameworks emerged through observations, reflections, lived
            experiences, and lots of{" "}
            <span className="italic font-serif text-bronze text-[1.3em]">
              internal creativity
            </span>{" "}
            being allowed to flow. They continue to be lived with, questioned,
            refined, and shared across different people, spaces, and contexts.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.15)}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-stretch"
        >
          {FRAMEWORKS.map((f, i) => (
            <motion.article
              key={f.title}
              variants={fadeUp}
              data-testid={`framework-card-${i}`}
              className="group relative bg-forestDeep/40 border border-bone/10 backdrop-blur-sm overflow-hidden h-full"
              style={{ borderTopLeftRadius: "200px", borderTopRightRadius: "200px" }}
            >
              <div className="pt-10 px-7 pb-10 h-full flex flex-col text-center">
                <span className="text-[10px] uppercase tracking-[0.3em] text-bronze">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {/* title — positioned high within the arch, top-aligned, all baselines share min-h block */}
                <div className="mt-3 min-h-[4.5rem] flex items-start justify-center">
                  <h3 className="font-serif text-[1.4rem] md:text-[1.5rem] leading-[1.1]">
                    {f.title}
                  </h3>
                </div>
                {/* subtitle — fixed height for cross-card alignment */}
                <div className="mt-2 min-h-[2.5rem] flex items-start justify-center">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-clay/80 leading-snug">
                    {f.subtitle}
                  </p>
                </div>
                <div className="my-5 w-10 h-px bg-bronze/40 mx-auto" />
                <p className="font-serif italic text-clay/95 text-lg leading-snug">
                  &ldquo;{f.quote}&rdquo;
                </p>
                <p className="text-sm text-bone/75 leading-relaxed font-light mt-4 flex-1">
                  {f.body}
                </p>
                {f.external ? (
                  <a
                    href={f.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`framework-cta-${i}`}
                    className="mt-8 text-[10px] uppercase tracking-[0.3em] text-bronze link-underline self-center"
                  >
                    {f.cta} →
                  </a>
                ) : (
                  <Link
                    to={`/journeys/${f.slug}`}
                    data-testid={`framework-cta-${i}`}
                    className="mt-8 text-[10px] uppercase tracking-[0.3em] text-clay link-underline self-center"
                  >
                    {f.cta} →
                  </Link>
                )}
              </div>

              {/* arched glow on hover */}
              <div className="absolute inset-x-6 top-6 h-40 rounded-t-full border border-bone/10 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none" />
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Frameworks;
